import React from "react";
import { useSettings } from "../contexts/SettingsContext";
import { CheckCircle2 } from "lucide-react";

interface PublicChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  metadata?: {
    type?:
      | "customer_service_rag"
      | "error_response"
      | "offline_response"
      | "offline_notice"
      | "fallback_response"
      | "agentic_notice"
      | "agentic_parameter_collection"
      | "agentic_ready"
      | "agentic_action_result"
      | "welcome";
    meta?: any;
    confidence_score?: number;
    context_chunks_used?: number;
    source_documents?: any[];
    response_metadata?: any;
    search_metadata?: any;
    intent?: {
      type: string;
      action: string;
      topic: string;
      entities?: Record<string, any>;
    };
    requires_auth?: boolean;
    eventEmitted?: boolean;
    [key: string]: any;
  };
  created_at: string;
}

interface Props {
  message: PublicChatMessage;
}

interface FlexibleMetadata {
  [key: string]: any;
}

const PublicAgenticResponse: React.FC<Props> = ({ message }) => {
  const { debugMode } = useSettings();

  const extractMetadata = () => {
    const metadata = (message.metadata || {}) as FlexibleMetadata;

    const findValueByKey = (
      obj: any,
      targetKey: string,
      visited = new Set()
    ): any => {
      if (!obj || typeof obj !== "object" || visited.has(obj)) return null;
      visited.add(obj);
      if (obj[targetKey] !== undefined && obj[targetKey] !== null)
        return obj[targetKey];
      for (const [_, value] of Object.entries(obj)) {
        if (typeof value === "object" && value !== null) {
          const found = findValueByKey(value, targetKey, visited);
          if (found !== null && found !== undefined) return found;
        }
      }
      return null;
    };

    const sources = [
      metadata.meta,
      metadata,
      metadata.result,
      metadata.action,
      metadata.details,
    ].filter(Boolean);

    const extractField = (fieldNames: string[]): any => {
      for (const source of sources) {
        for (const fieldName of fieldNames) {
          const value = findValueByKey(source, fieldName);
          if (value !== null && value !== undefined && value !== "")
            return value;
        }
      }
      return null;
    };

    return {
      topic: extractField(["topic", "actionTopic", "category", "domain"]),
      action: extractField([
        "action",
        "actionName",
        "action_name",
        "name",
        "operation",
        "method",
      ]),
      actionKey: extractField(["actionKey", "action_key", "key", "identifier"]),
      provider: extractField([
        "provider",
        "channel",
        "service",
        "system",
        "source",
      ]),
      flowName: extractField([
        "flowApiName",
        "flowName",
        "flow_name",
        "processName",
        "workflowName",
      ]),
      duration: extractField([
        "durationMs",
        "duration",
        "executionTime",
        "processingTime",
      ]),
      status: extractField(["ok", "success", "successful", "status", "state"]),
      inputs: extractField([
        "inputs",
        "input",
        "parameters",
        "params",
        "collected",
      ]),
      outputs: extractField([
        "output",
        "outputs",
        "result",
        "results",
        "response",
      ]),
      error: extractField([
        "error",
        "errorMessage",
        "err",
        "exception",
        "failure",
      ]),
      errorCode: extractField([
        "code",
        "errorCode",
        "error_code",
        "statusCode",
      ]),
      type: extractField(["type", "messageType", "resultType", "kind"]),
      rawMetadata: metadata,
    };
  };

  const meta = extractMetadata();

  // Generate friendly action name (same approach as your AgenticResponse)
  const generateActionName = (): string => {
    const actionSources = [
      meta.action,
      meta.actionKey,
      meta.topic && meta.action ? `${meta.topic} ${meta.action}` : null,
      meta.flowName
        ? String(meta.flowName)
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (l: string) => l.toUpperCase())
        : null,
      meta.type === "agentic_action_result" ? "Agentic Action" : null,
      meta.type === "flow_execution" ? "Flow Execution" : null,
      "System Action",
    ];
    const result = actionSources.find(
      (source) => source && typeof source === "string" && String(source).trim()
    );
    return String(result || "Unknown Action");
  };

  const generateChannel = (): string => {
    const channelSources = [
      meta.provider,
      meta.flowName ? "salesforce.flow" : null,
      meta.outputs && typeof meta.outputs === "object" ? "api" : null,
      "system",
    ];
    const result = channelSources.find(
      (source) => source && typeof source === "string"
    );
    return String(result || "unknown");
  };

  const isSuccess = (): boolean => {
    if (meta.error || meta.errorCode) return false;
    if (
      meta.status === true ||
      meta.status === "success" ||
      meta.status === "completed"
    )
      return true;
    if (
      meta.status === false ||
      meta.status === "failed" ||
      meta.status === "error"
    )
      return false;
    if (
      meta.outputs &&
      typeof meta.outputs === "object" &&
      Object.keys(meta.outputs).length > 0
    )
      return true;
    return !meta.error;
  };

  const formatSuccessData = (data: any): React.ReactNode => {
    if (!data) return null;

    // If outputs is a string, show as-is
    if (typeof data === "string") {
      return (
        <p className="text-gray-100 text-base leading-relaxed whitespace-pre-wrap">
          {data}
        </p>
      );
    }

    // Check common message keys
    const candidates = [
      "outputMessage",
      "Output Message",
      "output",
      "message",
      "result",
      "resultMessage",
    ];
    for (const key of candidates) {
      if (data[key]) {
        return (
          <p className="text-gray-100 text-base leading-relaxed whitespace-pre-wrap">
            {String(data[key])}
          </p>
        );
      }
    }

    // If object contains small-ish fields, show them nicely
    const entries = Object.entries(data).filter(
      ([k, v]) => v !== null && v !== undefined && v !== ""
    );
    if (entries.length === 1) {
      return (
        <p className="text-gray-100 text-base leading-relaxed whitespace-pre-wrap">
          {String(entries[0][1])}
        </p>
      );
    }

    // Fallback: pretty-print JSON
    return (
      <pre className="text-sm text-gray-200 whitespace-pre-wrap">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  const extractFriendlyFields = () => {
    const outputs = meta.outputs ?? {};
    const inputs = meta.inputs ?? {};

    const successMessage =
      outputs?.OutputMessage ||
      outputs?.outputMessage ||
      outputs?.message ||
      outputs?.output ||
      (typeof outputs === "string" ? outputs : null) ||
      "Your request has been successfully processed";

    const caseNumber =
      outputs?.CaseNumber ||
      outputs?.caseNumber ||
      outputs?.case_number ||
      outputs?.id ||
      null;

    const capturedParams: { [key: string]: any } = {
      Subject: inputs?.Subject || inputs?.subject,
      "Customer Email": inputs?.CustomerEmail || inputs?.customerEmail,
      Name: inputs?.Name || inputs?.name,
      Description: inputs?.Description || inputs?.description,
      "Business ID": inputs?.BusinessID || inputs?.businessID,
      "Phone Number": inputs?.PhoneNumber || inputs?.phoneNumber,
    };

    return { successMessage, caseNumber, capturedParams };
  };

  const actionName = generateActionName();
  const channel = generateChannel();
  const success = isSuccess();
  const { successMessage, caseNumber, capturedParams } =
    extractFriendlyFields();

  const hasCapturedParams = Object.values(capturedParams).some(
    (v) => v !== null && v !== undefined && v !== ""
  );
  const useFriendlyDisplay = success && hasCapturedParams;

  const bgColorClass = success
    ? "bg-gradient-to-br from-green-600/20 via-emerald-700/10 to-green-900/20 border-green-600/30"
    : "bg-gradient-to-br from-red-700/20 via-rose-800/10 to-red-900/20 border-red-600/30";

  return (
    <div
      className={`relative p-5 rounded-xl border shadow-lg overflow-hidden ${bgColorClass}`}
    >
      {/* Animated glow for success */}
      {success && (
        <div className="absolute inset-0 rounded-xl border-2 border-green-500/10 animate-pulse pointer-events-none" />
      )}

      {/* Debug header — only shown when debugMode is true */}
      {debugMode && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`relative flex h-3 w-3 ${
                success ? "bg-green-400" : "bg-red-400"
              } rounded-full`}
            >
              <span
                className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${
                  success
                    ? "animate-ping bg-green-400"
                    : "animate-ping bg-red-400"
                }`}
              />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white tracking-wide">
                Agentic Action Result
              </h3>
              <div className="text-xs text-gray-300">
                Action:{" "}
                <span className="text-white font-medium">{actionName}</span>
                {meta.flowName && (
                  <span className="text-gray-400 ml-1">
                    (Flow: {String(meta.flowName)})
                  </span>
                )}
                {channel && (
                  <span className="text-gray-400 ml-1">via {channel}</span>
                )}
              </div>
            </div>
          </div>

          {meta.duration && (
            <div className="text-xs text-gray-300 italic">
              ⏱ {String(meta.duration)}ms
            </div>
          )}
        </div>
      )}

      <div className="p-3">
        {/* Friendly display (captured params) */}
        {useFriendlyDisplay ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="text-green-400 h-5 w-5 animate-pulse" />
              <h4 className="text-base font-semibold text-green-200 tracking-wide">
                Operation Completed Successfully
              </h4>
            </div>
            <p className="text-gray-100 text-base mb-4 leading-relaxed">
              {successMessage}
            </p>

            {/* {caseNumber && (
              <p className="text-gray-100 mb-3">
                <strong className="text-white">Case Number:</strong>{" "}
                {caseNumber}
              </p>
            )} */}

            {/* <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">
                Captured Information:
              </h4>
              <div className="bg-[#0f1720] p-3 rounded-lg border border-[#1f2a37]">
                {Object.entries(capturedParams).map(([label, val]) =>
                  val ? (
                    <p key={label} className="text-sm text-gray-200 break-all">
                      <strong className="text-white">{label}:</strong>{" "}
                      {String(val)}
                    </p>
                  ) : null
                )}
              </div>
            </div> */}
          </>
        ) : (
          <>
            {/* show detailed success outputs */}
            {success && meta.outputs && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                  <h4 className="text-sm font-semibold text-green-200">
                    Operation Completed Successfully
                  </h4>
                </div>

                <div className="text-gray-100 text-base leading-relaxed whitespace-pre-wrap">
                  {formatSuccessData(meta.outputs)}
                </div>
              </div>
            )}

            {/* inputs (if present) */}
            {meta.inputs &&
              typeof meta.inputs === "object" &&
              Object.keys(meta.inputs).length > 0 && (
                <div className="mt-3 bg-[#0f1720] p-3 rounded-lg border border-[#1f2a37]">
                  <h5 className="text-xs font-medium text-gray-400 mb-2">
                    Input Parameters:
                  </h5>
                  <div className="text-sm text-gray-200">
                    {Object.entries(meta.inputs).map(([k, v]) => (
                      <p key={k} className="break-all">
                        <strong className="text-gray-200">{k}:</strong>{" "}
                        {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
          </>
        )}

        {/* error block */}
        {!success && meta.error && (
          <div className="mt-4 p-3 rounded-lg bg-red-900/10 border border-red-600/20">
            <h4 className="text-sm font-semibold text-red-200 mb-2">
              Operation Failed
            </h4>
            <p className="text-sm text-red-100">{String(meta.error)}</p>
            {meta.errorCode && (
              <p className="text-xs text-red-400 mt-1">
                Code: {String(meta.errorCode)}
              </p>
            )}
          </div>
        )}

        {/* context footer */}
        {debugMode && (
          <div className="mt-4 text-xs text-gray-400">
            <strong className="text-gray-300">Action:</strong> {actionName}
            {meta.flowName && (
              <span className="text-gray-400 ml-1">
                {" "}
                (Flow: {String(meta.flowName)})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicAgenticResponse;
